'use client';

import {
  CarouselComponent as Carousel,
  CarouselConfiguration,
} from '@vaad1st/component.organism.carousel';
import classNames from 'classnames';
import React, { useEffect, useState } from 'react';
import ContentCardComponent, {
  ParentComponent,
} from '../content-card/content-card.component';
import GalleryCardComponent from '../gallery-card/gallery-card.component';
import TableCard from '../table-card/table-card.component';
import styles from './carousel.component.module.less';

type CarouselCardType = 'content-card' | 'destination-card' | 'table-card';

type SerializableCarouselChild = {
  cardType?: CarouselCardType;
  ['sling:resourceType']?: string;
  [key: string]: any;
};

const isCarouselCardType = (value: any): value is CarouselCardType =>
  value === 'content-card' ||
  value === 'destination-card' ||
  value === 'table-card';

const isSerializableChild = (child: any): child is SerializableCarouselChild =>
  !!child && typeof child === 'object' && !React.isValidElement(child);

const containsSerializableChildren = (child: any): boolean => {
  if (Array.isArray(child)) {
    return child.some(containsSerializableChildren);
  }

  return isSerializableChild(child);
};

const flattenChildCollection = (child: any): any[] => {
  if (Array.isArray(child)) {
    return child.reduce<any[]>(
      (acc, current) => acc.concat(flattenChildCollection(current)),
      [],
    );
  }

  return child !== undefined && child !== null ? [child] : [];
};

function renderCardComponent(
  cardProps: any,
  cardType: CarouselCardType,
  itemIndex: number = 0,
) {
  switch (cardType) {
    case 'destination-card':
      return (
        <GalleryCardComponent
          key={`Carousel--child--gcc--${itemIndex}`}
          {...cardProps}
        />
      );
    case 'table-card':
      return (
        <TableCard key={`Carousel--child--tcc--${itemIndex}`} {...cardProps} />
      );

    case 'content-card':
      return (
        <ContentCardComponent
          key={`Carousel--child--ccc--${itemIndex}`}
          parentComponent={ParentComponent.Carousel}
          {...cardProps}
        />
      );
    default:
      return null;
  }
}

interface CarouselComponentProps {
  children?:
    | React.ReactNode
    | React.ReactNode[]
    | SerializableCarouselChild
    | SerializableCarouselChild[];
  extendedXlLayout?: boolean;
  configuration?: CarouselConfiguration;
  controlsConfig?: {
    arrows?: boolean;
    byScreen?: boolean;
    byDrag?: boolean;
    lazyLoading?: boolean;
  };
  extendedPadding?: boolean;
  animateIn?: boolean;
}

export default function CarouselComponent({
  extendedPadding = true,
  animateIn = true,
  ...rest
}: CarouselComponentProps) {
  const [hydratedAnimateIn, setHydratedAnimateIn] = useState(false);
  const propsCopy = { ...rest };

  propsCopy.extendedXlLayout = !propsCopy.extendedXlLayout;
  propsCopy.configuration =
    propsCopy.configuration || CarouselConfiguration.Heavy;
  propsCopy.children = []; // Clear out children to use correct components
  // Invert the AEM flag and backlog renaming it.
  propsCopy.controlsConfig = {
    byDrag: true,
    ...(rest.controlsConfig || {}),
    lazyLoading: false,
  };

  const normalizedChildren: any[] = containsSerializableChildren(rest.children)
    ? flattenChildCollection(rest.children)
    : React.Children.toArray(rest.children as React.ReactNode);

  const children = normalizedChildren
    .map((child: any, index: number) => {
      if (!child) {
        return null;
      }

      if (isSerializableChild(child)) {
        if (isCarouselCardType(child.cardType)) {
          return renderCardComponent(child, child.cardType, index);
        }

        const resourceType = child['sling:resourceType'];
        const componentType =
          typeof resourceType === 'string'
            ? resourceType.split('/').pop()
            : undefined;

        if (componentType && isCarouselCardType(componentType)) {
          return renderCardComponent(child, componentType, index);
        }

        return null;
      }

      if (React.isValidElement(child)) {
        const resourceType = (child.props as any)?.['sling:resourceType'];
        const componentType =
          typeof resourceType === 'string'
            ? resourceType.split('/').pop()
            : undefined;

        if (componentType && isCarouselCardType(componentType)) {
          return renderCardComponent(child.props, componentType, index);
        }

        return child;
      }

      return null;
    })
    .filter((c: any) => c !== null) as React.ReactElement[];

  propsCopy.children = children;

  useEffect(() => {
    // Keep SSR content visible, then restore the authored animation setting.
    setHydratedAnimateIn(animateIn);
  }, [animateIn]);

  // Trigger resize event once after the final render
  useEffect(() => {
    if (children.length > 0) {
      window.dispatchEvent(new Event('resize'));
    }
  }, [children]);

  return (
    <div
      className={classNames(styles['carousel'], {
        [styles['carousel--extended-padding']]: extendedPadding,
      })}>
      <Carousel {...{ ...propsCopy, children }} animateIn={hydratedAnimateIn} />
    </div>
  );
}
